/**
 * Fila de promociones bajo el carrusel (ADM-05): un bloque ancho y dos
 * angostos, de lado a lado de la pantalla.
 *
 * Cada bloque es un pequeno carrusel propio: el primero rota entre las piezas
 * 1, 4, 7…; el segundo entre 2, 5, 8…; el tercero entre 3, 6, 9…. Asi el
 * administrador carga las piezas en orden y la fila se arma sola; con tres
 * piezas o menos nada rota y cada bloque muestra la suya.
 *
 * Las piezas anchas van al primer bloque y las angostas a los otros dos: el
 * ancho lo decide la posicion, no la imagen, y por eso el panel avisa que en
 * esta fila se alternan una pieza ancha y dos angostas.
 */
import type { Banner } from '@bandu/compartido';

import { useRotacion } from '../../hooks/use-rotacion';
import { LaminaBanner } from './LaminaBanner';

/** Bloques de la fila: el primero ancho, los otros dos angostos. */
const BLOQUES = 3;
/** Cuanto se queda cada pieza; distinto del carrusel para que no cambien todos a la vez. */
const INTERVALO_MS = 7500;

export interface FilaPromocionesProps {
  banners: Banner[];
}

export function FilaPromociones({ banners }: FilaPromocionesProps) {
  if (banners.length === 0) return null;

  const bloques = Array.from({ length: BLOQUES }, (_, bloque) =>
    banners.filter((_, indice) => indice % BLOQUES === bloque),
  ).filter((piezas) => piezas.length > 0);

  return (
    <ul className="promociones" aria-label="Promociones destacadas">
      {bloques.map((piezas, bloque) => (
        <li key={bloque} className={bloque === 0 ? 'promocion ancha' : 'promocion'}>
          <BloqueRotativo piezas={piezas} />
        </li>
      ))}
    </ul>
  );
}

function BloqueRotativo({ piezas }: { piezas: Banner[] }) {
  const { actual, manejadores } = useRotacion(piezas.length, INTERVALO_MS);

  return (
    <div className="promocion-pista" {...manejadores}>
      {piezas.map((banner, indice) => (
        <div
          key={banner.id}
          className={indice === actual ? 'promocion-lamina activa' : 'promocion-lamina'}
          aria-hidden={indice !== actual}
          inert={indice !== actual}
        >
          <LaminaBanner banner={banner} />
        </div>
      ))}
    </div>
  );
}
